import { Lock, Plus, X } from 'lucide-react'
import {
  CHOICE_TYPES,
  EXPRESSION_FUNCTIONS,
  FIELD_PATTERNS,
  FIELD_TYPES,
  FILE_FORMATS,
  FILE_MAX_SIZE_MB,
  READONLY_SOURCES,
  slugifyKey,
  type Expression,
  type FieldPattern,
  type FieldValidation,
  type FileFormat,
  type FormField,
  type ReadonlySource,
} from '@concours/shared'
import { Button, Input, Select, Textarea } from '@concours/ui'
import { ConditionEditor } from './ConditionEditor'

interface Props {
  field: FormField
  onChange: (next: FormField) => void
  /** Champs situés AVANT (conditions, calculs). */
  before: FormField[]
  /** Clé déjà publiée : elle ne doit plus changer (continuité des données entre versions). */
  keyLocked: boolean
  sections: { key: string; title: string }[]
  sectionKey: string
  onMoveSection: (key: string) => void
  onRemove: () => void
}

const num = (v: string) => (v === '' ? undefined : Number(v))

/** Panneau de propriétés d'un champ (constructeur). */
export function FieldProperties({
  field,
  onChange,
  before,
  keyLocked,
  sections,
  sectionKey,
  onMoveSection,
  onRemove,
}: Props) {
  const set = (patch: Partial<FormField>) => onChange({ ...field, ...patch })
  const setValidation = (patch: Partial<FieldValidation>) => {
    const next = { ...field.validation, ...patch }
    for (const k of Object.keys(next) as (keyof FieldValidation)[])
      if (next[k] === undefined || next[k] === '') delete next[k]
    set({ validation: Object.keys(next).length ? next : undefined })
  }
  const v = field.validation ?? {}
  const system = field.type === 'computed' || field.type === 'readonly'

  return (
    <div className="stack" style={{ gap: 'var(--space-4)' }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>{FIELD_TYPES[field.type].label}</strong>
        <Button variant="ghost" size="sm" icon={<X />} onClick={onRemove}>
          Supprimer le champ
        </Button>
      </div>

      <Input
        label="Libellé"
        required
        value={field.label}
        onChange={(e) => {
          const label = e.target.value
          // La clé suit le libellé tant qu'elle n'a jamais été publiée et qu'elle en dérivait.
          const follow =
            !keyLocked && (field.key === slugifyKey(field.label) || !field.label.trim())
          set({ label, ...(follow && label.trim() ? { key: slugifyKey(label) } : {}) })
        }}
      />
      <Input
        label="Clé technique"
        mono
        value={field.key}
        disabled={keyLocked}
        hint={
          keyLocked ? (
            <span className="row" style={{ gap: 4 }}>
              <Lock size={12} aria-hidden="true" /> Déjà publiée : la clé ne change plus (continuité
              des données).
            </span>
          ) : (
            'Minuscules, chiffres et _. Stable d’une version à l’autre.'
          )
        }
        onChange={(e) => set({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
      />
      <Textarea
        label="Description"
        rows={2}
        value={field.description ?? ''}
        onChange={(e) => set({ description: e.target.value || undefined })}
      />
      <Input
        label="Texte d’aide"
        value={field.help ?? ''}
        onChange={(e) => set({ help: e.target.value || undefined })}
      />
      {!system && (
        <label className="check-row">
          <input
            type="checkbox"
            checked={Boolean(field.required)}
            onChange={(e) => set({ required: e.target.checked || undefined })}
          />
          <span>Obligatoire</span>
        </label>
      )}
      <Select
        label="Section"
        value={sectionKey}
        options={sections.map((s) => ({ value: s.key, label: s.title }))}
        onChange={(e) => onMoveSection(e.target.value)}
      />

      {CHOICE_TYPES.includes(field.type) && <OptionsEditor field={field} onChange={onChange} />}

      {(field.type === 'text' || field.type === 'textarea') && (
        <fieldset className="prop-group">
          <legend>Contrôles</legend>
          <div className="form-grid">
            <Input
              label="Longueur min."
              type="number"
              min={0}
              value={v.minLength ?? ''}
              onChange={(e) => setValidation({ minLength: num(e.target.value) })}
            />
            <Input
              label="Longueur max."
              type="number"
              min={1}
              value={v.maxLength ?? ''}
              onChange={(e) => setValidation({ maxLength: num(e.target.value) })}
            />
          </div>
          <Select
            label="Format"
            value={v.pattern ?? ''}
            options={[
              { value: '', label: 'Libre' },
              ...Object.entries(FIELD_PATTERNS).map(([value, p]) => ({ value, label: p.label })),
            ]}
            onChange={(e) =>
              setValidation({ pattern: (e.target.value || undefined) as FieldPattern | undefined })
            }
            hint="Liste fermée de formats sûrs (pas d’expression régulière libre)."
          />
        </fieldset>
      )}

      {(field.type === 'integer' || field.type === 'decimal') && (
        <fieldset className="prop-group">
          <legend>Contrôles</legend>
          <div className="form-grid">
            <Input
              label="Minimum"
              type="number"
              value={v.min ?? ''}
              onChange={(e) => setValidation({ min: num(e.target.value) })}
            />
            <Input
              label="Maximum"
              type="number"
              value={v.max ?? ''}
              onChange={(e) => setValidation({ max: num(e.target.value) })}
            />
          </div>
          <Input
            label="Unité affichée"
            value={field.unit ?? ''}
            onChange={(e) => set({ unit: e.target.value || undefined })}
          />
        </fieldset>
      )}

      {field.type === 'date' && (
        <fieldset className="prop-group">
          <legend>Contrôles</legend>
          {(['minDate', 'maxDate'] as const).map((k) => (
            <div
              key={k}
              className="row"
              style={{ gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}
            >
              <Input
                label={k === 'minDate' ? 'Au plus tôt' : 'Au plus tard'}
                type="date"
                disabled={v[k] === 'today'}
                value={v[k] && v[k] !== 'today' ? v[k] : ''}
                onChange={(e) => setValidation({ [k]: e.target.value || undefined })}
              />
              <label className="check-row" style={{ paddingBlockEnd: 'var(--space-2)' }}>
                <input
                  type="checkbox"
                  checked={v[k] === 'today'}
                  onChange={(e) => setValidation({ [k]: e.target.checked ? 'today' : undefined })}
                />
                <span>Aujourd’hui</span>
              </label>
            </div>
          ))}
        </fieldset>
      )}

      {field.type === 'multiselect' && (
        <fieldset className="prop-group">
          <legend>Contrôles</legend>
          <div className="form-grid">
            <Input
              label="Choix min."
              type="number"
              min={0}
              value={v.minItems ?? ''}
              onChange={(e) => setValidation({ minItems: num(e.target.value) })}
            />
            <Input
              label="Choix max."
              type="number"
              min={1}
              value={v.maxItems ?? ''}
              onChange={(e) => setValidation({ maxItems: num(e.target.value) })}
            />
          </div>
        </fieldset>
      )}

      {(field.type === 'file' || field.type === 'image') && (
        <fieldset className="prop-group">
          <legend>Pièce jointe</legend>
          <div className="row" style={{ gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            {(Object.keys(FILE_FORMATS) as FileFormat[])
              .filter((f) => field.type === 'file' || f !== 'pdf')
              .map((format) => {
                const formats = field.file?.formats ?? []
                return (
                  <label key={format} className="check-row">
                    <input
                      type="checkbox"
                      checked={formats.includes(format)}
                      onChange={(e) =>
                        set({
                          file: {
                            maxSizeMb: field.file?.maxSizeMb ?? 5,
                            formats: e.target.checked
                              ? [...formats, format]
                              : formats.filter((f) => f !== format),
                          },
                        })
                      }
                    />
                    <span>{FILE_FORMATS[format].label}</span>
                  </label>
                )
              })}
          </div>
          <Input
            label="Taille maximale (Mo)"
            type="number"
            min={1}
            max={FILE_MAX_SIZE_MB}
            value={field.file?.maxSizeMb ?? 5}
            onChange={(e) =>
              set({
                file: { formats: field.file?.formats ?? [], maxSizeMb: Number(e.target.value) },
              })
            }
          />
        </fieldset>
      )}

      {field.type === 'computed' && (
        <ComputeEditor field={field} onChange={onChange} before={before} />
      )}

      {field.type === 'readonly' && (
        <Select
          label="Valeur affichée"
          value={field.source ?? ''}
          options={Object.entries(READONLY_SOURCES).map(([value, label]) => ({ value, label }))}
          onChange={(e) => set({ source: e.target.value as ReadonlySource })}
          hint="Fournie par le système au moment du dépôt ; le candidat ne peut pas la modifier."
        />
      )}

      <fieldset className="prop-group">
        <legend>Champ conditionnel</legend>
        <ConditionEditor
          value={field.visibility}
          onChange={(visibility) => set({ visibility })}
          fields={before}
          subject="du champ"
        />
      </fieldset>
    </div>
  )
}

function OptionsEditor({
  field,
  onChange,
}: {
  field: FormField
  onChange: (f: FormField) => void
}) {
  const options = field.options ?? []
  const setOptions = (next: typeof options) => onChange({ ...field, options: next })
  return (
    <fieldset className="prop-group">
      <legend>Options</legend>
      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        {options.map((o, i) => (
          <div key={i} className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
            <Input
              label={i === 0 ? 'Libellé' : undefined}
              aria-label={`Libellé de l’option ${i + 1}`}
              fieldClassName="grow"
              value={o.label}
              onChange={(e) => {
                const label = e.target.value
                const follow = o.value === slugifyKey(o.label) || !o.value
                setOptions(
                  options.map((x, j) =>
                    j === i ? { label, value: follow ? slugifyKey(label) : x.value } : x
                  )
                )
              }}
            />
            <Input
              label={i === 0 ? 'Valeur' : undefined}
              aria-label={`Valeur de l’option ${i + 1}`}
              mono
              value={o.value}
              style={{ width: '8rem' }}
              onChange={(e) =>
                setOptions(options.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))
              }
            />
            <Button
              variant="ghost"
              iconOnly
              icon={<X />}
              aria-label={`Retirer l’option ${i + 1}`}
              onClick={() => setOptions(options.filter((_, j) => j !== i))}
            />
          </div>
        ))}
        <div>
          <Button
            variant="secondary"
            size="sm"
            icon={<Plus />}
            onClick={() => {
              const n = options.length + 1
              setOptions([...options, { label: `Option ${n}`, value: `option_${n}` }])
            }}
          >
            Ajouter une option
          </Button>
        </div>
      </div>
    </fieldset>
  )
}

function ComputeEditor({
  field,
  onChange,
  before,
}: {
  field: FormField
  onChange: (f: FormField) => void
  before: FormField[]
}) {
  const expr = field.compute
  const set = (compute: Expression) => onChange({ ...field, compute })
  const dates = before.filter((f) => f.type === 'date')
  const numbers = before.filter((f) => ['integer', 'decimal', 'computed'].includes(f.type))
  const listed = expr && (expr.fn === 'sum' || expr.fn === 'count_filled') ? expr.fields : []
  const candidates = expr?.fn === 'sum' ? numbers : before.filter((f) => f.type !== 'readonly')
  return (
    <fieldset className="prop-group">
      <legend>Calcul</legend>
      <Select
        label="Règle"
        value={expr?.fn ?? 'age_at'}
        options={Object.entries(EXPRESSION_FUNCTIONS).map(([value, label]) => ({ value, label }))}
        onChange={(e) => {
          const fn = e.target.value as Expression['fn']
          if (fn === 'age_at')
            set({ fn, field: dates[0]?.key ?? '', at: `${new Date().getFullYear()}-12-31` })
          else if (fn === 'concat') set({ fn, parts: [], separator: ' ' })
          else set({ fn, fields: [] })
        }}
      />
      {expr?.fn === 'age_at' && (
        <>
          <Select
            label="Date de naissance (champ)"
            value={expr.field}
            placeholder={dates.length ? '— Choisir —' : 'Aucun champ date situé avant'}
            options={dates.map((f) => ({ value: f.key, label: f.label }))}
            onChange={(e) => set({ ...expr, field: e.target.value })}
          />
          <Input
            label="Âge apprécié au"
            type="date"
            value={typeof expr.at === 'string' ? expr.at : ''}
            hint="Date fixée par le règlement du concours (ex. 31 décembre de l’année)."
            onChange={(e) => set({ ...expr, at: e.target.value })}
          />
          <Input
            label="Unité affichée"
            value={field.unit ?? ''}
            placeholder="ans"
            onChange={(e) => onChange({ ...field, unit: e.target.value || undefined })}
          />
        </>
      )}
      {(expr?.fn === 'sum' || expr?.fn === 'count_filled') && (
        <div className="choice-options">
          {candidates.length ? (
            candidates.map((f) => (
              <label key={f.key} className="check-row">
                <input
                  type="checkbox"
                  checked={listed.includes(f.key)}
                  onChange={(e) =>
                    set({
                      ...expr,
                      fields: e.target.checked
                        ? [...listed, f.key]
                        : listed.filter((k) => k !== f.key),
                    })
                  }
                />
                <span>{f.label}</span>
              </label>
            ))
          ) : (
            <p className="text-muted">Aucun champ utilisable situé avant.</p>
          )}
        </div>
      )}
      {expr?.fn === 'concat' && (
        <>
          <div className="choice-options">
            {before
              .filter((f) => ['text', 'select', 'radio', 'computed'].includes(f.type))
              .map((f) => {
                const has = expr.parts.some((p) => 'field' in p && p.field === f.key)
                return (
                  <label key={f.key} className="check-row">
                    <input
                      type="checkbox"
                      checked={has}
                      onChange={(e) =>
                        set({
                          ...expr,
                          parts: e.target.checked
                            ? [...expr.parts, { field: f.key }]
                            : expr.parts.filter((p) => !('field' in p && p.field === f.key)),
                        })
                      }
                    />
                    <span>{f.label}</span>
                  </label>
                )
              })}
          </div>
          <Input
            label="Séparateur"
            value={expr.separator ?? ' '}
            onChange={(e) => set({ ...expr, separator: e.target.value })}
          />
        </>
      )}
    </fieldset>
  )
}
